import {
  Card,
  CardMedia,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import strings from '../../common/Translation/Translate';
import ClearIcon from '@mui/icons-material/Clear';
import PrimaryButton from '../../components/Button/PrimaryButton';
import ReadMore from '../../components/ReadMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageGallery from 'react-image-gallery';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectedVenuesForCosting, setSelectedVenuesForCosting } from 'utils/redux/reducer/costing-slice';
import { fetchFromStorage } from 'utils/storage';
import { getBaseUrl } from 'utils/validators/HelperFunctions';
import HelpIcon from '@mui/icons-material/Help';
import { ToolTip } from 'components/Tooltip/ToolTip';
import Currency from 'utils/hooks/getCurrency';
import PDFViewerModal from 'components/Iframe';
import Popup from '../../components/Popup';
import axiosInstance from 'utils/redux/axios';
import { URLS } from 'utils/constants/urls';
import PageLoader from 'components/PageLoader';

const VenueLayout = ({ layouts }: { layouts: any }) => {
  const theme = useTheme();
  const screenSizeUpMd = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Grid container spacing={2}>
      {layouts.map((item: any) => (
        <Grid item key={item.id} mt={1}>
          <Card
            sx={{
              //width: "9vw",
              border: `1px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.secondary.dark,
            }}>
            <Grid container alignItems={'center'} justifyContent={'space-between'} gap={1} pr={1}>
              <Grid item>
                <CardMedia
                  component="img"
                  sx={{
                    borderRight: `6px solid ${theme.palette.primary.main}`,
                    height: screenSizeUpMd ? '4vw' : '6vh',
                    width: screenSizeUpMd ? '6vw' : '10vw',
                    objectFit: 'fill',
                  }}
                  image={getBaseUrl() + item.imgUrl}
                  alt={''}
                />
              </Grid>
              <Grid item>
                <Typography fontWeight={'bold'} fontSize={'0.62rem'}>
                  {item.maxCapacity}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

type VenueDescriptionProps = {
  description: any;
  width: any;
  length: any;
  height: any;
  area: any;
  layouts: any;
  floorPlan: any;
  hourlyPricing: boolean;
  pricePerHour: any;
};

export const VenueDescription = ({
  description,
  width,
  length,
  height,
  area,
  layouts,
  floorPlan,
  hourlyPricing,
  pricePerHour,
}: VenueDescriptionProps) => {
  const theme = useTheme();
  const screenSizeUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const getVenueAreaDetails = () => {
    const arr = [];
    if (width) {
      arr.push(`${strings.widthText}: ${width} ${strings.venue_text_length_unit}`);
    }
    if (length) {
      arr.push(`${strings.lengthText}: ${length} ${strings.venue_text_length_unit}`);
    }
    if (height) {
      arr.push(`${strings.heightText}: ${height} ${strings.venue_text_length_unit}`);
    }
    if (area) {
      arr.push(`${strings.areaText}: ${area} ${strings.venue_text_unit}`);
    }
    if (floorPlan) {
      arr.push('');
    }
    return arr.join(' | ');
  };
  return (
    <>
      {description && (
        <Typography fontSize="0.75rem">
          <ReadMore text={description} idealLength={screenSizeUpMd ? 550 : 170} />
        </Typography>
      )}
      {hourlyPricing && (
        <Box bgcolor={theme.palette.secondary.light} p={1} my={1} border={`solid 1px ${theme.palette.secondary.main}`}>
          <Typography fontSize="0.65rem" fontWeight={500}>
            {`${strings.hourlyPricingText1} ${Currency(pricePerHour || 0)} ${strings.hourlyPricingText2}`}
          </Typography>
        </Box>
      )}
      {(width || length || height || area || floorPlan) && (
        <Box bgcolor={theme.palette.secondary.light} p={1} my={1} border={`solid 1px ${theme.palette.secondary.main}`}>
          <Typography fontSize="0.65rem" fontWeight={500}>
            {getVenueAreaDetails()}
            {floorPlan && (
              <Link target={'_blank'} href={getBaseUrl() + floorPlan} underline="hover" color={'secondary'}>
                {strings.floorPlan}
              </Link>
            )}
          </Typography>
        </Box>
      )}
      {layouts && <VenueLayout layouts={layouts} />}
    </>
  );
};
const AvailablePackagesComponent = ({
  menuPlanUrl,
  prices,
  setSelectedPricePackage,
  selectedVenues,
  date,
  venueData,
  selectedPricePackage,
}: any) => {
  const theme = useTheme();
  const [searchText, setSearchText] = useState('');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menuModal, setMenuModal] = useState<boolean>(false);
  const [pricePackageDescription, setPricePackageDescription] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();

  const handlePricePackageChange = (id: number | null) => {
    if (id) {
      setSelectedPricePackage(id);
      const arr = selectedVenues.map((item: any) => {
        if (item.date === date) {
          const arr1 = item.venues.map((vItem: any) => {
            if (vItem.id === venueData.id) {
              return { ...vItem, pricePackageId: id };
            }
            return vItem;
          });
          return { ...item, venues: arr1 };
        }
        return item;
      });
      dispatch(setSelectedVenuesForCosting(arr));
    }
  };

  const handlePricePackageInfoClick = async () => {
    if (!selectedPricePackage) return;
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getPricePackageById(selectedPricePackage));
      if (status === 200) {
        setPricePackageDescription(data?.description || '');
      }
    } catch {
      setPricePackageDescription('');
    } finally {
      setIsLoading(false);
    }
  };

  const getPackageLabel = (priceItem: any) => {
    const priceLabel =
      priceItem.pricePerPerson !== 0
        ? `${strings.perPersonText}: ${priceItem.formattedPricePerPerson}`
        : priceItem.hallRental > 0
        ? `${strings.hallRental}: ${priceItem.formattedHallRental}`
        : '';
    return priceLabel ? `${priceItem.pricePackageName} - ${priceLabel}` : priceItem.pricePackageName;
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <Box mt={1.5} display="flex" flexDirection="column" gap={1.5}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item>
            <Typography className="font-cinzel" variant="subtitle1" fontWeight={600} color={'primary'}>
              {strings.availablePackageText}
            </Typography>
          </Grid>
          {menuPlanUrl && (
            <Grid item>
              <Box
                onClick={() => setMenuModal(true)}
                p={0.5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  textTransform: 'capitalize',
                  borderRadius: '4px',
                  border: `1px solid ${theme.palette.primary.main}`,
                  ':hover': {
                    cursor: 'pointer',
                    boxShadow: '0 5px 5px rgba(0, 0, 0, 0.1)',
                  },
                }}>
                <FormatListBulletedOutlinedIcon sx={{ mr: 1, fontSize: '0.87rem' }} color={'primary'} />
                <Typography fontWeight={500} color={'primary'} fontSize={'0.75rem'}>
                  {strings.menuPlanText}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 0 }}>
            <Select
              value={selectedPricePackage ?? ''}
              displayEmpty
              onChange={e => handlePricePackageChange(e.target.value ? Number(e.target.value) : null)}
              onClose={() => {
                setSearchText('');
              }}
              MenuProps={{
                autoFocus: false,
                disableAutoFocusItem: true,
                PaperProps: {
                  sx: {
                    maxHeight: isMobile ? '50vh' : 350,
                    overflowY: 'auto',
                  },
                },
                MenuListProps: {
                  autoFocusItem: false,
                  sx: {
                    maxHeight: isMobile ? '50vh' : 350,
                    overflowY: 'auto',
                    p: 0,
                  },
                },
              }}
              renderValue={selected => {
                if (!selected) {
                  return (
                    <Typography fontSize="0.75rem" color="text.secondary">
                      {strings.rfq_modal_text_price_package}
                    </Typography>
                  );
                }

                const selectedItem = prices.find((item: any) => item.pricePackageId === selected);

                return <Typography fontSize="0.75rem">{selectedItem ? getPackageLabel(selectedItem) : ''}</Typography>;
              }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              }}>
              {/* Search Input */}
              <Box
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                sx={{
                  p: 1,
                  position: 'sticky',
                  top: 0,
                  bgcolor: '#fff',
                  zIndex: 2,
                }}>
                <TextField
                  autoFocus
                  size="small"
                  placeholder="Search package..."
                  fullWidth
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  InputProps={{
                    endAdornment: searchText ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            setSearchText('');
                          }}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>

              {prices
                .filter((priceItem: any) => {
                  const label = getPackageLabel(priceItem)?.toLowerCase() || '';
                  const search = searchText.toLowerCase().trim();

                  return label.includes(search);
                })
                .sort((a: any, b: any) => {
                  const aLabel = getPackageLabel(a)?.toLowerCase() || '';
                  const bLabel = getPackageLabel(b)?.toLowerCase() || '';
                  const search = searchText.toLowerCase().trim();

                  const aStarts = aLabel.startsWith(search);
                  const bStarts = bLabel.startsWith(search);

                  if (aStarts && !bStarts) return -1;
                  if (!aStarts && bStarts) return 1;

                  return 0;
                })
                .map((priceItem: any) => (
                  <MenuItem
                    key={priceItem.pricePackageId}
                    value={priceItem.pricePackageId}
                    sx={{
                      maxWidth: '100%',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      py: 1,
                    }}>
                    <Typography fontSize="0.75rem">{getPackageLabel(priceItem)}</Typography>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {selectedPricePackage && (
            <Popup
              onClick={handlePricePackageInfoClick}
              Component={InfoOutlinedIcon}
              text={pricePackageDescription}
              tooltipText=""
            />
          )}
        </Box>
      </Box>
      {menuModal && <PDFViewerModal pdfUrl={menuPlanUrl} setMenuModal={setMenuModal} menuModal={menuModal} />}
    </>
  );
};

const BookOrRemoveBtn = ({ venueData, selectedPricePackage, date }: any) => {
  const dispatch = useDispatch();
  const selectedVenues = useSelector(selectedVenuesForCosting);
  const searchData = JSON.parse(fetchFromStorage('searchFormData', true) as string);

  const handleBookNow = () => {
    let array = [...selectedVenues];
    const obj = {
      ...venueData,
      bookingDate: date,
      layout: '',
      addOns: [],
      addOnList: venueData.addOns,
      pricePackageId: selectedPricePackage,
      guaranteedGuests: searchData.guests,
      expectedGuests: Math.round(
        parseInt(searchData.guests == '' ? '0' : searchData.guests) * (1 + venueData.excessGuestsPercentage / 100),
      ),
    };
    let idx = array.findIndex((item: any) => item.date === date);
    if (idx != -1) {
      array[idx] = { date: date, venues: [...array[idx].venues, obj] };
    } else {
      array.push({
        date: date,
        venues: [obj],
      });
    }
    dispatch(setSelectedVenuesForCosting(array));
  };

  const handleRemove = () => {
    const array = selectedVenues
      .map((item: any) => {
        if (item.date === date) {
          const a = item.venues.filter((v: any) => v.id !== venueData.id);
          return { date: item.date, venues: a };
        }
        return item;
      })
      .filter((item: any) => item.venues.length > 0);
    console.log('array: ', array);
    dispatch(setSelectedVenuesForCosting(array));
  };

  return !selectedVenues?.find(
    (item: any) => item.date === date && item.venues.find((v: any) => v.id === venueData.id),
  ) ? (
    <PrimaryButton onClick={handleBookNow}>{strings.bookNowText}</PrimaryButton>
  ) : (
    <PrimaryButton onClick={handleRemove}>{strings.removeText}</PrimaryButton>
  );
};

type VenueDetailsProps = {
  venueData: any;
  date: any;
  onRfqModalOpen: any;
  menuPlanUrl: string;
};

export const VenueDetails = ({ venueData, date, onRfqModalOpen, menuPlanUrl }: VenueDetailsProps) => {
  const theme = useTheme();
  const selectedVenues = useSelector(selectedVenuesForCosting);
  const isVenueSelected = Boolean(
    selectedVenues?.find((item: any) => item.date === date && item.venues.find((v: any) => v.id === venueData.id)),
  );
  const abc =
    selectedVenues.find((item: any) => item.date === date)?.venues.find((vItem: any) => vItem.id === venueData.id)
      ?.pricePackageId || null;
  const [selectedPricePackage, setSelectedPricePackage] = useState<number | null>(abc);

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            'img[class=image-gallery-image]': {
              width: '-webkit-fill-available',
            },
          }}>
          {venueData.media.otherImageUrls.length >= 1 ? (
            <ImageGallery
              showThumbnails={venueData.media.otherImageUrls.length > 1}
              showPlayButton={false}
              autoPlay
              showNav={false}
              slideInterval={10000}
              items={venueData.media.otherImageUrls.map((item: any) => ({
                original: getBaseUrl() + item,
                thumbnail: getBaseUrl() + item,
              }))}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: 'lightgrey',
                height: '160px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Typography>{strings.noImageAvailableText}</Typography>
            </Box>
          )}
          {venueData.terms ? (
            <Link href={getBaseUrl() + venueData.terms} underline="always" alignItems="center" color={'#000000'}>
              <Grid container alignItems={'flex-start'} justifyContent={'center'} my={1}>
                <Grid item mr={1}>
                  <DescriptionOutlinedIcon />
                </Grid>
                <Grid item>
                  <Typography>
                    <b>{strings.termsConditionText}</b>
                  </Typography>
                </Grid>
              </Grid>
            </Link>
          ) : null}
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2} justifyContent="space-between" mb={2}>
            <Grid item>
              <Typography fontWeight="bold" color="primary" variant="h6">
                {venueData.name}
              </Typography>
              {venueData.bookingStatus === 'WAITLISTED' && (
                <Box mt={1} display={'flex'} gap={1} alignItems={'center'}>
                  <Box
                    sx={{ border: `1px solid ${theme.palette.primary.main}`, bgcolor: theme.palette.secondary.light }}
                    py={0.25}
                    px={0.5}>
                    <Typography fontSize={'10px'} sx={{ letterSpacing: 1 }}>
                      {strings.venueWaitlistedText}
                    </Typography>
                  </Box>
                  <ToolTip title={venueData.waitListInfo}>
                    <HelpIcon fontSize="small" color="primary" />
                  </ToolTip>
                </Box>
              )}
              {venueData.bookingStatus === 'AVAILABLE' && (
                <Box mt={1} display={'flex'} gap={1} alignItems={'center'}>
                  <Box
                    sx={{ border: `1px solid ${theme.palette.primary.main}`, bgcolor: theme.palette.secondary.light }}
                    py={0.25}
                    px={0.5}>
                    <Typography fontSize={'10px'} sx={{ letterSpacing: 1 }}>
                      {strings.venueAvailableText}
                    </Typography>
                  </Box>
                  {isVenueSelected && <CheckCircleIcon fontSize="small" sx={{ color: '#2e7d32' }} />}
                </Box>
              )}
            </Grid>
            {!venueData.onlyRFQ ? (
              <Grid item>
                <BookOrRemoveBtn venueData={venueData} selectedPricePackage={selectedPricePackage} date={date} />
              </Grid>
            ) : (
              <Grid item>
                <PrimaryButton
                  onClick={() => {
                    const selectedVenueForRfq = { id: venueData.id, name: venueData.name };
                    onRfqModalOpen(selectedVenueForRfq);
                  }}>
                  {strings.rfq_modal_text_title_name}
                </PrimaryButton>
              </Grid>
            )}
          </Grid>
          <VenueDescription
            description={venueData.description}
            width={venueData.width}
            length={venueData.length}
            height={venueData.height}
            area={venueData.area}
            layouts={venueData.layouts}
            floorPlan={venueData.floorPlan}
            hourlyPricing={venueData.hourlyPricing}
            pricePerHour={venueData.pricePerHour}
          />
          {!venueData.hourlyPricing && (
            <AvailablePackagesComponent
              menuPlanUrl={menuPlanUrl}
              prices={venueData.prices}
              setSelectedPricePackage={setSelectedPricePackage}
              selectedVenues={selectedVenues}
              date={date}
              venueData={venueData}
              selectedPricePackage={selectedPricePackage}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};
